/**
 * CheckInForm — Submit a progress update for a KPI assignment.
 *
 * Fields: value, notes, optional financial impact, file upload for evidence.
 */
import React, { useState, useRef } from 'react';
import Send from 'lucide-react/dist/esm/icons/send';
import Paperclip from 'lucide-react/dist/esm/icons/paperclip';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import X from 'lucide-react/dist/esm/icons/x';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import { Button, TextField, TextArea, Alert } from '../../../components/ui';
import { useKPIStore } from '../../../stores/kpiStore';
import type { KPIAssignment, EvidenceUploadResponse } from '../../../services/api/kpi';

interface CheckInFormProps {
  assignment: KPIAssignment;
  onSubmit: (assignmentId: string, data: {
    value?: number; notes?: string;
    evidence_ids?: string[]; financial_impact_value?: number;
  }) => Promise<void>;
  onClose: () => void;
}

const CheckInForm: React.FC<CheckInFormProps> = ({ assignment, onSubmit, onClose }) => {
  const [value, setValue] = useState<string>(
    assignment.current_value !== null ? String(assignment.current_value) : ''
  );
  const [notes, setNotes] = useState('');
  const [financialImpact, setFinancialImpact] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<EvidenceUploadResponse[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadEvidence } = useKPIStore();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    setError(null);
    try {
      // We need a temporary check-in ID for upload — upload after check-in is created
      // For now, collect files and upload after check-in creation
      const newFiles: EvidenceUploadResponse[] = [];
      for (const file of Array.from(files)) {
        if (file.size > 50 * 1024 * 1024) {
          setError(`File "${file.name}" exceeds 50MB limit`);
          continue;
        }
        newFiles.push({
          id: crypto.randomUUID(),
          s3_key: '',
          original_filename: file.name,
          mime_type: file.type,
          file_size: file.size,
          extracted_text: null,
          _file: file, // Store raw file for upload after check-in
        } as EvidenceUploadResponse & { _file: File });
      }
      setUploadedFiles(prev => [...prev, ...newFiles]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = (idx: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(assignment.id, {
        value: value ? parseFloat(value) : undefined,
        notes: notes.trim() || undefined,
        financial_impact_value: financialImpact ? parseFloat(financialImpact) : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to submit check-in');
    } finally {
      setSaving(false);
    }
  };

  const unit = assignment.definition_unit_label || '';
  const target = assignment.target_value;
  const hasFinancialImpact = assignment.financial_impact_target !== null && assignment.financial_impact_target !== undefined;

  return (
    <div className="p-4 rounded-xl bg-surface-primary border border-default space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-content">Check In</h4>
        <button
          onClick={onClose}
          className="text-xs text-content-muted hover:text-content-tertiary cursor-pointer border-none outline-none"
        >
          Cancel
        </button>
      </div>

      {/* Value input */}
      <div>
        <div className="flex items-center gap-2">
          <TextField
            label={`Current Value${unit ? ` (${unit})` : ''}`}
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={target ? `Target: ${target}` : 'Enter value...'}
            autoFocus
            className="flex-1"
          />
          {target && value && (
            <span className="text-xs text-content-muted tabular-nums whitespace-nowrap mt-5">
              {Math.round(((parseFloat(value) - (assignment.baseline_value || 0)) / (target - (assignment.baseline_value || 0))) * 100)}%
            </span>
          )}
        </div>
      </div>

      {/* Financial Impact (shown when assignment has a target) */}
      {hasFinancialImpact && (
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-content-muted mt-5 shrink-0" />
          <TextField
            label="Financial Impact (this period)"
            type="number"
            step="any"
            value={financialImpact}
            onChange={(e) => setFinancialImpact(e.target.value)}
            placeholder={`Target: ${assignment.financial_impact_target}`}
            className="flex-1"
          />
        </div>
      )}

      <TextArea
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        placeholder="What drove this number? Any context for your manager..."
        resize="none"
      />

      {/* Evidence Upload */}
      <div>
        <label className="text-[10px] text-content-muted uppercase tracking-wider block mb-1.5">
          Evidence (optional)
        </label>
        <div className="space-y-2">
          {uploadedFiles.map((f, idx) => (
            <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-secondary/50 border border-subtle">
              <FileText size={14} className="text-content-muted shrink-0" />
              <span className="text-xs text-content-secondary flex-1 truncate">{f.original_filename}</span>
              <span className="text-[10px] text-content-muted">{(f.file_size / 1024).toFixed(0)} KB</span>
              <button
                onClick={() => removeFile(idx)}
                className="text-content-muted hover:text-status-error cursor-pointer border-none outline-none p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-subtle text-xs text-content-muted hover:text-content-tertiary hover:border-default transition-colors cursor-pointer w-full outline-none"
          >
            <Paperclip size={14} />
            {uploading ? 'Uploading...' : 'Attach files (PDF, images, spreadsheets)'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xlsx,.docx,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end">
        <Button
          variant="success"
          size="lg"
          onClick={handleSubmit}
          loading={saving}
          disabled={!value}
          leftIcon={!saving ? <Send size={14} /> : undefined}
        >
          Submit Check-in
        </Button>
      </div>
    </div>
  );
};

export default React.memo(CheckInForm);
